import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  Chip,
  CircularProgress,   
  IconButton,   
  Menu,   
  MenuItem,   
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

import { 
  GetAuthoritiesCERT,
  DeleteAuthorityCERT
} from '../../../redux/actions/certification/authoritiesActions';

import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import AuthoritiesModal from './AuthoritiesModal';

import global from "../../../global.scss";

const statusChipColor = (status) => {
  switch (status) {
    case "ACTIVE":           return "success";
    case "INVITED":          return "info";
    case "CREATED":          return "info";
    case "INACTIVE":         return "warning";
    case "INVITED_EXPIRED":  return "error";
    case "SUSPENDED":        return "error";
    default:                 return "default";
  }
};

const AuthoritiesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.certification);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [authoritiesModalOpen, setAuthoritiesModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [authoritiesList, setAuthoritiesList] = useState([]);
  const [authoritiesTotal, setAuthoritiesTotal] = useState(0);

  const [authoritiesMenuOpen, setAuthoritiesMenuOpen] = useState(false);
  const [authoritiesMenuAnchor, setAuthoritiesMenuAnchor] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  // TODO: update columns and sort keys when columns are decided
  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Username",
      sort: "user_name",
    },
    {
      label: "Authority Name",
      sort: "authority_name",
    },
    {
      label: "Name",
      sort: null,
    },
    {
      label: "Email",
      sort: "primary_email",
    },
    {
      label: "Type",
      sort: "type",
    },
    {
      label: "Status",
      sort: "status",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    },
    {
      label: "",
      sort: null,
    },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetAuthorities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatAuthoritiesList(state.authoritiesList.records || []);
      setAuthoritiesTotal(state.authoritiesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatAuthoritiesList = (list) => {
    setAuthoritiesList(list);
  };

  async function handleGetAuthorities(){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetAuthoritiesCERT(page+1, rowsPerPage, query, setLoading));
  };

  const handleDelete = () => {
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteAuthorityCERT(deleteId, setDeleting)).then(() => {
      handleGetAuthorities().then(() => {
        setDeleteId(null);
      });
    });
  };

  return (
    <Box>

      <AuthoritiesModal 
        open={authoritiesModalOpen}
        setOpen={setAuthoritiesModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetAuthorities}
        />

      <ConfirmationModal 
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
        />

      <TopBar
        title={"Authorities"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text="Create new authority"
        button1OnClick={() => {
          setModalTitle("Create Authority");
          setUpdate(null);
          setAuthoritiesModalOpen(true);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          authoritiesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No authorities found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                authoritiesList.map((row, index) => (
                  <TableRow 
                    key={index}
                    className="table__clickable-row">
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        {row.id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.user_name || <NA />}</TableCell>
                    <TableCell>{row.authority_name || <NA />}</TableCell>
                    <TableCell>{(row.first_name || row.last_name) ? `${row.first_name} ${row.last_name}`.trim() : <NA />}</TableCell>
                    <TableCell>{row.primary_email || <NA />}</TableCell>
                    <TableCell>{row.type || <NA />}</TableCell>
                    <TableCell>
                      {
                        row.status
                        ?
                        <Chip
                          variant="filled"
                          size="small"
                          color={statusChipColor(row.status)}
                          label={row.status.replace(/_/g, " ")}
                          />
                        :
                        <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        row.id === deleteId && deleting
                        ?
                        <CircularProgress size={26} sx={{ margin: "7px" }}/>
                        :
                        <IconButton
                          onClick={(e) => {
                            setAuthoritiesMenuAnchor(e.currentTarget);
                            setAuthoritiesMenuOpen(true);
                            setUpdate(row);
                            setModalTitle(row.id || "");
                            setDeleteId(row.id);
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={authoritiesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={authoritiesMenuOpen}
        anchorEl={authoritiesMenuAnchor}
        onClose={() => setAuthoritiesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setAuthoritiesMenuOpen(false);
            setAuthoritiesModalOpen(!authoritiesModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        {
          update?.is_active === false
          &&
          <MenuItem
            onClick={() => {
              setAuthoritiesMenuOpen(false);
              setDeleteConfirmationModalOpen(true);
            }}>
            <Box
              className="flexCenter_Row"
              gap={"10px"}>
              <DeleteIcon />
              Delete
            </Box>
          </MenuItem>
        }
      </Menu>
    </Box>
  )
}

export default AuthoritiesPage;